import React from 'react';
import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation, useNavigate } from 'react-router';
import ModelsAccess from './pages/ModelsAccess';
import Examples from './pages/Examples';
import ManageMSI from './pages/ManageMSI';
import Debug from './pages/Debug';
import Batch from './pages/Batch';
import TokenUsage from './pages/TokenUsage';
import Welcome from './pages/Welcome';
import { useMsalAuth } from './hooks/useMsalAuth';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminLayout from './pages/admin/AdminLayout.tsx';
import AdminApis from './pages/admin/ApisPage.tsx';
import BackendsPage from './pages/admin/BackendsPage.tsx';
import ResourcePoolsPage from './pages/admin/ResourcePoolsPage.tsx';
import ConfigDriftPage from './pages/admin/ConfigDriftPage.tsx';
import ModelApprovalsPage from './pages/admin/ModelApprovalsPage.tsx';
import TaskSuitePage from './pages/admin/TaskSuitePage.tsx';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AppProvider, useEnvironment } from './contexts/AppContext';
import { useMsal, AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { useSwaAuth } from './hooks/useSwaAuth';
import { useQueryClient } from '@tanstack/react-query';
import { Open16Regular } from '@fluentui/react-icons';
import { Badge, Button, makeStyles, tokens } from '@fluentui/react-components';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
  },
  header: {
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    padding: tokens.spacingVerticalM,
    '@media (max-width: 768px)': {
      padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
    },
  },
  headerInner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    '@media (max-width: 768px)': {
      flexWrap: 'wrap',
    },
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    '@media (max-width: 768px)': {
      flexWrap: 'wrap',
      width: '100%',
    },
  },
  brand: {
    fontWeight: tokens.fontWeightBold,
    whiteSpace: 'nowrap',
    '@media (max-width: 480px)': { fontSize: tokens.fontSizeBase300 },
  },
  nav: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
    '@media (max-width: 768px)': {
      width: '100%',
      gap: '2px',
    },
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    '@media (max-width: 768px)': {
      width: '100%',
      justifyContent: 'flex-end',
    },
  },
  email: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    '@media (max-width: 480px)': { display: 'none' },
  },

  main: {
    flex: 1,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    paddingBottom: tokens.spacingVerticalXXXL,
    '@media (max-width: 768px)': {
      paddingLeft: tokens.spacingHorizontalXS,
      paddingRight: tokens.spacingHorizontalXS,
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '50vh',
  },
  loadingText: {
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
  adminLoading: {
    padding: tokens.spacingVerticalL,
  },
  footer: {
    position: 'fixed',
    left: '0',
    bottom: '0',
    width: '100%',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    padding: tokens.spacingVerticalS,
    fontSize: tokens.fontSizeBase200,
    textAlign: 'center',
    backgroundColor: tokens.colorNeutralBackground2,
    zIndex: 100,
  },
  footerLink: {
    color: tokens.colorBrandForeground1,
    textDecoration: 'none',
    marginRight: tokens.spacingHorizontalM,
  },
  footerLinkLast: {
    color: tokens.colorBrandForeground1,
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
  },
  helpIcon: {
    marginLeft: tokens.spacingHorizontalXS,
  },
});

const RETURN_PATH_KEY = 'trapi_return_path';

/** When unauthenticated, saves the current path and redirects to login page */
function SavePathAndRedirect() {
  const location = useLocation();
  const fullPath = location.pathname + location.search + location.hash;
  if (fullPath && fullPath !== '/') {
    try { sessionStorage.setItem(RETURN_PATH_KEY, fullPath); } catch { /* quota */ }
  }
  return <Navigate to="/" replace />;
}

/** After login, redirects to the saved path (if any) then clears it */
function PostLoginRedirect({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const didRedirect = React.useRef(false);

  React.useEffect(() => {
    if (didRedirect.current) return;
    try {
      const saved = sessionStorage.getItem(RETURN_PATH_KEY);
      if (saved && saved !== '/' && location.pathname === '/') {
        sessionStorage.removeItem(RETURN_PATH_KEY);
        didRedirect.current = true;
        navigate(saved, { replace: true });
      } else {
        sessionStorage.removeItem(RETURN_PATH_KEY);
      }
    } catch { /* ignore */ }
  }, [navigate, location.pathname]);

  return <>{children}</>;
}

export default function App() {
  const styles = useStyles();

  const linkStyle = ({ isActive }: { isActive: boolean }) => ({
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium,
    textDecoration: 'none' as const,
    color: isActive ? tokens.colorNeutralBackground1 : 'inherit',
    background: isActive ? tokens.colorBrandForeground1 : 'transparent',
    fontWeight: tokens.fontWeightSemibold,
    fontSize: 'inherit',
    whiteSpace: 'nowrap' as const,
  });

  const { account, login, logout } = useMsalAuth();
  const { inProgress } = useMsal();
  const { isAdmin, loading: adminCheckLoading } = useSwaAuth();
  const qc = useQueryClient();

  const authReady = inProgress === 'none';

  return (
    <BrowserRouter>
      <AppProvider>
        <div className={styles.root}>
          <header className={styles.header}>
            <div className={styles.headerInner}>
              <div className={styles.headerLeft}>
                <div className={styles.brand}>TRAPI Self Service</div>
                <EnvBadge />
                <AuthenticatedTemplate>
                  <nav className={styles.nav}>
                    <NavLink to="/models" style={linkStyle}>Models</NavLink>
                    <NavLink to="/examples" style={linkStyle}>Examples</NavLink>
                    <NavLink to="/managemsi" style={linkStyle}>Identities</NavLink>
                    <NavLink to="/batch" style={linkStyle}>Batch</NavLink>
                    <NavLink to="/tokenusage" style={linkStyle}>Token Usage</NavLink>
                    <NavLink to="/debug" style={linkStyle}>Debug</NavLink>
                    {isAdmin && <NavLink to="/admin" style={linkStyle}>Admin</NavLink>}
                  </nav>
                </AuthenticatedTemplate>
              </div>
              <div className={styles.headerRight}>
                <AuthenticatedTemplate>
                  <span className={styles.email}>{preferredEmailFromAccount(account)}</span>
                  <Button appearance="secondary" size="small" onClick={() => { qc.invalidateQueries(); }}>Refresh</Button>
                  <Button appearance="outline" size="small" onClick={() => logout()}>Sign out</Button>
                </AuthenticatedTemplate>
                <UnauthenticatedTemplate>
                  <Button appearance="primary" size="small" onClick={() => login()}>Sign in</Button>
                </UnauthenticatedTemplate>
              </div>
            </div>
          </header>

          <main className={styles.main}>
            {!authReady ? (
              <div className={styles.loadingContainer}>
                <div className={styles.loadingText}>Loading...</div>
              </div>
            ) : (
              <>
                <AuthenticatedTemplate>
                  <PostLoginRedirect>
                    <Routes>
                      <Route path="/" element={<Navigate to="/models" replace />} />
                      <Route path="/myaccess" element={<Navigate to="/models" replace />} />
                      <Route path="/models" element={<ModelsAccess />} />
                      <Route path="/examples" element={<Examples />} />
                      <Route path="/managemsi" element={<ManageMSI />} />
                      <Route path="/batch" element={<Batch />} />
                      <Route path="/tokenusage" element={<TokenUsage />} />
                      <Route path="/debug" element={<Debug />} />
                      <Route
                        path="/admin"
                        element={
                          adminCheckLoading ? (
                            <div className={styles.adminLoading}>Loading…</div>
                          ) : isAdmin ? (
                            <ErrorBoundary fallbackMessage="Admin page error">
                              <AdminLayout />
                            </ErrorBoundary>
                          ) : (
                            <Navigate to="/myaccess" replace />
                          )
                        }
                      >
                        <Route index element={<AdminDashboard />} />
                        <Route path="apis" element={<AdminApis />} />
                        <Route path="backends" element={<BackendsPage />} />
                        <Route path="pools" element={<ResourcePoolsPage />} />
                        <Route path="drift" element={<ConfigDriftPage />} />
                        <Route path="model-approvals" element={<ModelApprovalsPage />} />
                        <Route path="tasks" element={<TaskSuitePage />} />
                      </Route>
                      <Route path="*" element={<Navigate to="/models" replace />} />
                    </Routes>
                  </PostLoginRedirect>
                </AuthenticatedTemplate>
                <UnauthenticatedTemplate>
                  <Routes>
                    <Route path="/" element={<Welcome />} />
                    <Route path="*" element={<SavePathAndRedirect />} />
                  </Routes>
                </UnauthenticatedTemplate>
              </>
            )}
          </main>
          <footer className={styles.footer}>
            <a
              href="https://dev.azure.com/msresearch/TRAPI/_wiki/wikis/TRAPI.wiki/15198/Data-Policies-and-Privacy"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.footerLink}
            >Data &amp; Privacy Policies</a>
            <a
              href="http://go.microsoft.com/fwlink/?LinkId=518021"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.footerLink}
            >Data Privacy Notice</a>
            <a
              href="https://aka.ms/trapi/"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.footerLinkLast}
            >
              Help <Open16Regular className={styles.helpIcon} />
            </a>
          </footer>
        </div>
      </AppProvider>
    </BrowserRouter>
  );
}

function preferredEmailFromAccount(account: unknown): string {
  // Accept unknown, narrow cautiously to avoid property existence TS errors
  const acct = (account as { idTokenClaims?: Record<string, unknown>; username?: string }) || {};
  const claims = acct.idTokenClaims || {};
  const getStr = (k: string) => {
    const v = claims[k];
    return typeof v === 'string' ? v : '';
  };
  const preferred = getStr('preferred_username');
  const emailClaim = getStr('email');
  const upn = getStr('upn');
  const fallback = typeof acct.username === 'string' ? acct.username : '';
  return (preferred || emailClaim || upn || fallback || '').toString().trim();
}

function EnvBadge() {
  const { currentEnvironment, isDev } = useEnvironment();
  if (!isDev) return null;
  return <Badge appearance="filled" color="warning" size="small">{currentEnvironment.toUpperCase()}</Badge>;
}

// v2
